import React, { useEffect, useMemo } from 'react';

import { MENUS } from '../my-central/constants';
import { Transition } from 'react-transition-group';
import { Box } from '../../components/UI-CHAKRA/Box';
import { ReactComponent as DoctorIcon } from '../../assets/images/icons/doctor.svg';
import { ReactComponent as PatientDataIcon } from '../../assets/images/icons/patient_data.svg';
import { ReactComponent as HistoryIcon } from '../../assets/images/icons/history.svg';
import { ReactComponent as InteractionsIcon } from '../../assets/images/icons/interactions.svg';
import { ReactComponent as ModelsIcon } from '../../assets/images/icons/models.svg';
import { ReactComponent as HelpIcon } from '../../assets/images/icons/help.svg';
import { ReactComponent as LightIcon } from '../../assets/images/icons/light.svg';
import { Flex } from '../../components/UI-CHAKRA/Flex';
import { Button } from '../../components/UI-CHAKRA/Button';
import { Text } from '../../components/UI-CHAKRA/Text';
import { useIsMobile } from '../../helpers/useIsMobile';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/mevo/close-mono-color.svg';
import { ReactComponent as CellPhoneIcon } from '../../assets/images/icons/mevo/ic-cell-phone.svg';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { defaultWidthTransition, SIDEBAR_WIDTH, useMyCentralStore } from '../../stores/myCentralStore';
import { Anchor } from '../../components/notifications/Anchor';
import { ANCHOR_POS, ANCHORS } from '../../components/notifications/notifications';
import { get } from 'lodash';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { Divider } from '../../components/UI-CHAKRA/Divider';
import { UserSignUpResponse } from './service';

interface MyCentralProps {
  onSelectMenu: Function;
  open: boolean;
  canUsePrescriptionHistory: boolean;
  selectedMenu: string;
  userSignUpStatus?: UserSignUpResponse | null;
}

const defaultStyle = {
  transition: `all 450ms`,
};

const stateStyle = {
  entering: { transform: 'rotate(180deg)' },
  entered: { transform: 'rotate(180deg)' },
  exiting: { transform: 'rotate(0deg)' },
  exited: { transform: 'rotate(0deg)' },
};

interface StyledButtonProps {
  onClick(): void;
  title: string;
  dataTut: string;
  ariaLabel: string;
  label: string;
  isExpanded: boolean;
  Icon: React.ReactNode;
  hasNotification?: boolean;
  selected?: boolean;
  pulseAnimation?: boolean;
}

const StyledSideBarButton = (props: StyledButtonProps) => {
  return (
    <Button
      onClick={props.onClick}
      title={props.title}
      data-tut={props.dataTut}
      aria-label={props.ariaLabel}
      w={'100%'}
      borderRadius={'10px !important'}
      border={'1px solid #e9ecee !important'}
      backgroundColor={props.selected ? 'primary.main' : 'white'}
      fill={props.selected ? '#fff' : 'primary.main'}
      color={props.selected ? '#fff' : 'primary.main'}
      padding={0}
      minWidth={0}
      height={'40px'}
      display={'flex'}
      alignItems={'center'}
      justifyContent={props.isExpanded ? 'flex-start' : 'center'}
      p={'5px'}
      className={props.pulseAnimation ? 'timered-pulse-animation' : ''}
    >
      <Box>{props.Icon}</Box>

      {props.isExpanded && (
        <Text color={props.selected ? '#fff' : 'typography.dark'} fontSize={'12px'} ml={'3px'} fontWeight={'400'}>
          {props.label}
        </Text>
      )}
    </Button>
  );
};

function MyCentral(props: MyCentralProps) {
  const sideBarWidth = useMyCentralStore((state) => state.sideBarWidth);
  const setSideBarWidth = useMyCentralStore((state) => state.setSideBarWidth);

  const { onSelectMenu, open } = props;

  const isMobile = useIsMobile('sm');

  const { prescriberConfiguration } = usePrescriptionStore();
  const disablePharmacyNews = get(prescriberConfiguration, 'Features.disablePharmacyNews', false);

  const isSideBarExpanded = useMemo(() => {
    return sideBarWidth === SIDEBAR_WIDTH.EXPANDED;
  }, [sideBarWidth]);

  const SideBarActionButton = useMemo(() => {
    if (isMobile) {
      if (!open || props.selectedMenu === MENUS.OFFICE) return null;

      return (
        <Button
          variant={'ghost'}
          aria-label="Open Drawer"
          w={'100%'}
          boxShadow={'none'}
          p={'0px'}
          onClick={() => onSelectMenu(!open)}
        >
          <CloseIcon fill={'#3D0E6B'} style={{ width: '18px', height: '18px' }} />
        </Button>
      );
    }

    return (
      <Button
        variant={'ghost'}
        onClick={() => {
          if (sideBarWidth === SIDEBAR_WIDTH.EXPANDED) {
            setSideBarWidth(SIDEBAR_WIDTH.NOT_EXPANDED);
            return;
          }
          setSideBarWidth(SIDEBAR_WIDTH.EXPANDED);
        }}
        aria-label="Open Drawer"
        w={'100%'}
        boxShadow={'none'}
        p={'0px'}
        display={'flex'}
        justifyContent={'flex-start'}
      >
        <Transition in={!isSideBarExpanded} timeout={200}>
          {(state) => (
            <ChevronRightIcon
              color={'primary.main'}
              w={'20px'}
              h={'20px'}
              style={{
                ...defaultStyle,
                // @ts-ignore
                ...stateStyle[state],
              }}
            />
          )}
        </Transition>
      </Button>
    );
  }, [isMobile, setSideBarWidth, sideBarWidth, isSideBarExpanded, open, onSelectMenu, props.selectedMenu]);

  useEffect(() => {
    setSideBarWidth(isMobile ? SIDEBAR_WIDTH.NOT_EXPANDED : SIDEBAR_WIDTH.EXPANDED);

    return () => {
      setSideBarWidth(SIDEBAR_WIDTH.NOT_RENDERED);
    };
  }, [isMobile, setSideBarWidth]);

  return (
    <Flex
      position={'absolute'}
      top={0}
      right={0}
      width={sideBarWidth}
      height={'100%'}
      borderLeft={'1px solid #e9ecee'}
      flexDirection={'column'}
      alignItems={'center'}
      bg={'#fff'}
      zIndex={9}
      transition={defaultWidthTransition}
    >
      {SideBarActionButton}

      <Flex flexDirection={'column'} alignItems={'center'} padding={'0 4px'} w={'100%'} gap={'5px'}>
        <Flex flexDirection={'column'} alignItems={'flex-start'} w={'100%'} gap={'5px'}>
          {isSideBarExpanded && (
            <Text
              color={'typography.dark'}
              fontSize={'12px'}
              mb={'4px'}
              ml={'8px'}
              fontWeight={600}
              flexWrap={'nowrap'}
            >
              Paciente
            </Text>
          )}

          <StyledSideBarButton
            onClick={() => props.onSelectMenu(MENUS.PATIENT_DATA)}
            title="Informações do paciente"
            dataTut="patient-data-menu"
            ariaLabel="Informações do paciente"
            Icon={<PatientDataIcon style={{ width: '17px', height: '17px' }} />}
            label={'Perfil'}
            isExpanded={isSideBarExpanded}
            selected={props.selectedMenu === MENUS.PATIENT_DATA}
          />

          {props.canUsePrescriptionHistory && (
            <StyledSideBarButton
              onClick={() => props.onSelectMenu(MENUS.HISTORY)}
              title="Histórico"
              dataTut="history-menu"
              ariaLabel="Histórico"
              Icon={<HistoryIcon style={{ width: '16px', height: '16px' }} />}
              label={'Histórico'}
              isExpanded={isSideBarExpanded}
              selected={props.selectedMenu === MENUS.HISTORY}
            />
          )}

          <StyledSideBarButton
            onClick={() => props.onSelectMenu(MENUS.DRUG_INTERACTIONS)}
            title="Interações"
            dataTut="interactions-menu"
            ariaLabel="Interações"
            Icon={<InteractionsIcon style={{ width: '16px', height: '16px' }} />}
            label={'Interação'}
            isExpanded={isSideBarExpanded}
            selected={props.selectedMenu === MENUS.DRUG_INTERACTIONS}
          />
        </Flex>

        <Box width={'80%'} height={'1px'} bgColor={'#E9ECEE'} mt={'12px'} mb={'8px'} />

        <Flex flexDirection={'column'} alignItems={'flex-start'} w={'100%'} gap={'5px'}>
          {isSideBarExpanded && (
            <Text
              color={'typography.dark'}
              fontSize={'12px'}
              mb={'4px'}
              ml={'8px'}
              fontWeight={600}
              flexWrap={'nowrap'}
            >
              Prescritor
            </Text>
          )}

          <Anchor anchorName={ANCHORS.MY_CENTRAL_PRESCRIBER_DATA} position={ANCHOR_POS.LEFT}>
            <StyledSideBarButton
              onClick={() => props.onSelectMenu(MENUS.PRESCRIBER_DATA)}
              title="Informações do prescritor"
              dataTut="prescriber-data-menu"
              ariaLabel="Informações do prescritor"
              Icon={<DoctorIcon style={{ width: '18px', height: '18px' }} />}
              label={'Dados'}
              isExpanded={isSideBarExpanded}
              selected={props.selectedMenu === MENUS.PRESCRIBER_DATA}
            />
          </Anchor>

          <Anchor anchorName={ANCHORS.MY_CENTRAL_MODELS} position={ANCHOR_POS.LEFT}>
            <StyledSideBarButton
              onClick={() => props.onSelectMenu(MENUS.MODELS)}
              title="Meus modelos"
              dataTut="models-menu"
              ariaLabel="Meus modelos"
              Icon={<ModelsIcon style={{ width: '17px', height: '17px' }} />}
              label={'Modelos'}
              isExpanded={isSideBarExpanded}
              selected={props.selectedMenu === MENUS.MODELS}
            />
          </Anchor>

          <StyledSideBarButton
            onClick={() => props.onSelectMenu(MENUS.HELP)}
            title="Ajuda"
            dataTut="help-menu"
            ariaLabel="Ajuda"
            Icon={<HelpIcon style={{ width: '17px', height: '17px' }} />}
            label={'Ajuda'}
            isExpanded={isSideBarExpanded}
            selected={props.selectedMenu === MENUS.HELP}
          />
        </Flex>
        <Divider margin={'12px 0'} />
        {props.userSignUpStatus?.enabled && (
          <Anchor anchorName={ANCHORS.MY_CENTRAL_OFFICE} position={ANCHOR_POS.TOP_LEFT}>
            <StyledSideBarButton
              onClick={() => props.onSelectMenu(MENUS.OFFICE)}
              title="Mevo App"
              dataTut="office-menu"
              ariaLabel="Mevo App"
              Icon={<CellPhoneIcon style={{ width: '17px', height: '17px' }} />}
              label={'Mevo App'}
              isExpanded={isSideBarExpanded}
              selected={props.selectedMenu === MENUS.OFFICE}
              pulseAnimation
            />
          </Anchor>
        )}
        {!disablePharmacyNews && (
          <Anchor anchorName={ANCHORS.MY_CENTRAL_NEWS} position={ANCHOR_POS.TOP_LEFT}>
            <StyledSideBarButton
              onClick={() => props.onSelectMenu(MENUS.News)}
              title="Novidades"
              dataTut="news-menu"
              ariaLabel="Novidades"
              Icon={<LightIcon style={{ width: '17px', height: '17px' }} />}
              label={'Novidades'}
              isExpanded={isSideBarExpanded}
            />
          </Anchor>
        )}
      </Flex>
    </Flex>
  );
}

export default MyCentral;
